import config from 'appconfig';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { State } from 'model/State';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { messages } from '../messages';
import { AccountDetail } from '../duck/models';
import { selectDetails, selectIsOnBehalfOfEmail } from '../duck/selectors';
import { Dimensions, Font } from 'components/visuals';
import Button from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import { colors } from '@atlaskit/theme';

interface Props {
  accountDetails: AccountDetail;
  canUserSeeAccountDetails: boolean;
  endDate: Date;
  billingContacts: Array<{ name: string; email: string }>;
  technicalContacts: Array<{ name: string; email: string }>;
  productKey: string;
}
export enum ContactTypes {
  TECHNICAL = 'TECHNICAL',
  BILLING = 'BILLING',
}

const StyledTableData = styled.td<{ paddingLeft?: boolean }>`
  padding: ${({ paddingLeft }) =>
    paddingLeft
      ? `${Dimensions.units(2.5)}px !important`
      : `${Dimensions.units(2.5)}px ${Dimensions.units(2.5)}px ${Dimensions.units(
          2.5,
        )}px 0 !important`};

  font-size: ${Dimensions.units(1.75)}px;
`;

const StyledTextLabel = styled.p<{ width?: string }>`
  color: ${colors.N100};
  font-family: ${Font.displayFamily};
  width: ${(props) => (props.width ? props.width : `${Dimensions.units(3)}%`)};
  text-align: end;
`;

const StyledTextValueWrapper = styled.div`
  width: ${Dimensions.units(7.5)}%;
  text-align: start;
`;

const StyledTDSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Dimensions.units(1)}px;
`;

const StyledButtonsWrapper = styled.div`
  width: ${Dimensions.units(7)}%;
`;

const StyledPipeSymbol = styled.span`
  color: ${colors.N100};
  padding-right: ${Dimensions.units(0.5)}px;
`;

const StyledLink = styled.a`
  margin-left: ${Dimensions.units(0.6)}px;
  color: ${colors.B400};
  padding: ${Dimensions.units(0.5)}px;
  cursor: pointer;
`;

const StyledFlex = styled.div<{ width?: string; flexDirection?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.flexDirection ? 'column' : 'row')};
  gap: ${Dimensions.units(2)}px;
  padding: ${Dimensions.units(0.5)}px;
  align-items: baseline;
  width: ${(props) => (props.width ? props.width : '')};
`;

const FieldWrapper = styled.div`
  margin-right: ${Dimensions.units(2)}px;
`;

const AddContactWrapper = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: end;
  margin-bottom: ${Dimensions.units(2)}px;
`;

const StyledContactsHeading = styled.div`
  color: ${colors.N100};
  font-family: ${Font.displayFamily};
  text-align: start;
`;

const ContactsWrapper = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  width: ${Dimensions.units(11)}%;
`;

const StyledSenInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const StyledDivWithBorder = styled.div`
  margin-top: ${Dimensions.units(3)}px;
  border-bottom: 1px solid ${colors.N100};
`;

const StyledSpan = styled.span<{ margin?: string; padding?: string }>`
  margin: ${(props) => (props.margin ? props.margin : '')};
  padding: ${(props) => (props.padding ? props.padding : '')};
`;

const hostedAccountTypes = [
  'HOSTED',
  'HOSTED_ADDON',
  'ONDEMAND_ADDON',
  'SUBSCRIPTION',
  'SUBSCRIPTION_ADDON',
];

const getContacts = (
  accountDetails: AccountDetail,
  contactType: string,
  contactsArray: Array<{ name: string; email: string }>,
) => {
  if (accountDetails) {
    accountDetails?.contactRelationships?.forEach((contact: any) => {
      if (contact.relationshipType === contactType)
        contactsArray.push({
          name: contact.contact.contactDetails.displayName,
          email: contact.contact.contactDetails.email,
        });
    });
  }
};

const getCancelActionLabel = (accountDetails: AccountDetail) => {
  let message;

  if (
    !accountDetails?.accountProvisioningDate &&
    accountDetails?.parentAccount &&
    (accountDetails?.merchandise.productPackage.key !== 'com.atlassian.bitbucket' ||
      accountDetails?.merchandise.productPackage.key !== 'atlassian.organisation')
  ) {
    message = <FormattedMessage {...messages.cancelSubscription} />;
  }

  if (accountDetails?.evaluation) {
    message = <FormattedMessage {...messages.cancelTrial} />;
  }
  return message;
};

const getDeactivatedAccountMessage = (accountDetails: AccountDetail) => {
  let message;
  if (accountDetails?.evaluation) {
    message = (
      <FormattedMessage
        {...messages.deactivatedEndedTrial}
        values={{
          convertLink: (
            <a href={config.root}>
              <FormattedMessage {...messages.convert} />
            </a>
          ),
          contactLink: (
            <a href="https://www.atlassian.com/company/contact">
              <FormattedMessage {...messages.contactUs} />
            </a>
          ),
        }}
      />
    );
  }
  if (accountDetails?.renewalAction === 'NONE') {
    message = (
      <FormattedMessage
        {...messages.deactivatedNoRenewalAction}
        values={{
          contactLink: (
            <a href="https://www.atlassian.com/company/contact">
              <FormattedMessage {...messages.contactUs} />
            </a>
          ),
        }}
      />
    );
  }
  if (accountDetails?.renewalAction && accountDetails?.renewalAction !== 'NONE') {
    message = (
      <FormattedMessage
        {...messages.deactivatedProvidePayment}
        values={{
          paymentLink: (
            <a href={`${config.root}/my/products/paymentdetails/${accountDetails.id}`}>
              <FormattedMessage {...messages.provideValidPaymentDetails} />
            </a>
          ),
          br: <br />,
          contactLink: (
            <a href="https://www.atlassian.com/company/contact">
              <FormattedMessage {...messages.contactUs} />
            </a>
          ),
        }}
      />
    );
  }
  return message;
};

const getAccountStatus = (accountDetails: AccountDetail) =>
  accountDetails?.renewalAction === 'NONE' ? (
    <FormattedMessage {...messages.inactive} />
  ) : (
    <FormattedMessage {...messages.active} />
  );

const getEndDateLabel = (accountDetails: AccountDetail) =>
  accountDetails?.renewalAction === 'AUTO_RENEW' && accountDetails?.creditCardData ? (
    <FormattedMessage {...messages.renewsOn} />
  ) : (
    <FormattedMessage {...messages.expirationDate} />
  );

const DetailsRow = ({ label, value, dataCy = '' }) => {
  return (
    <StyledFlex {...(dataCy ? { 'data-cy': dataCy } : {})} width={`${Dimensions.units(12.5)}%`}>
      <StyledTextLabel>{label}</StyledTextLabel>
      <StyledTextValueWrapper>
        <StyledSpan padding={`0 ${Dimensions.units(1.25)}px`}>{value}</StyledSpan>
      </StyledTextValueWrapper>
    </StyledFlex>
  );
};

const PaymentDetailsSection = ({ accountDetails, productKey }) => {
  const card = accountDetails?.creditCardData;
  const currentDate = new Date();

  const shouldShowEditCard =
    card?.gateway !== 'BRAINTREE_PAYPAL' &&
    new Date(accountDetails?.latestPeriod.endDate) > currentDate;

  const orgOrSiteValue = productKey !== 'atlassian.organisation' ? 's' : 'o';

  const isOnBehalfOfEmail: any = useSelector((state) => selectIsOnBehalfOfEmail(state as State));

  const paymentDetailsLink =
    accountDetails?.txAMigrated ||
    accountDetails?.entSubMigrated ||
    accountDetails?.productsBilledInCCP
      ? `${config.adminHubUrl}/${orgOrSiteValue}/${accountDetails?.externalUuid}/billing/paymentdetails`
      : `${config.root}/my/products/updatecreditcard?accountId=${accountDetails?.accountId}`;

  return (
    <>
      {(!accountDetails?.partnerManaged || isOnBehalfOfEmail.contactPartnerForAccount) && (
        <StyledFlex width={`${Dimensions.units(12.5)}%`}>
          <StyledTextLabel>
            <FormattedMessage {...messages.paymentMethod} />
          </StyledTextLabel>
          {card ? (
            <div>
              <StyledSpan data-cy="card-details" padding={`0 ${Dimensions.units(1.25)}px`}>
                {`${card.type} - ${card.maskedNumber} (${card.expiryMonth}/${card.expiryYear}) - ${card.name}`}
              </StyledSpan>
              {shouldShowEditCard && (
                <>
                  <StyledPipeSymbol>|</StyledPipeSymbol>
                  <StyledLink href={paymentDetailsLink}>
                    <FormattedMessage {...messages.editCreditCard} />
                  </StyledLink>
                </>
              )}
            </div>
          ) : (
            <StyledLink data-cy="add-payment-method-btn" href={paymentDetailsLink || ''}>
              <FormattedMessage {...messages.providePaymentDetails} />
            </StyledLink>
          )}
        </StyledFlex>
      )}
    </>
  );
};

const HostedAccountsDetails = ({ accountDetails, productKey }) => {
  return (
    <>
      {accountDetails?.deactivated ? (
        <DetailsRow
          label={
            accountDetails?.evaluation ? (
              <FormattedMessage {...messages.trial} />
            ) : (
              <FormattedMessage {...messages.subscription} />
            )
          }
          value={getDeactivatedAccountMessage(accountDetails)}
        />
      ) : (
        <>
          <StyledFlex width={`${Dimensions.units(12.5)}%`}>
            <StyledTextLabel>
              {accountDetails?.evaluation ? (
                <FormattedMessage {...messages.trial} />
              ) : (
                <FormattedMessage {...messages.subscription} />
              )}
            </StyledTextLabel>
            <StyledTextValueWrapper>
              <StyledSpan data-cy="account-status" padding={`0 ${Dimensions.units(1.25)}px`}>
                {getAccountStatus(accountDetails)}
              </StyledSpan>
              <StyledPipeSymbol>|</StyledPipeSymbol>
              {!accountDetails?.parentAccount && (
                <FormattedMessage {...messages.cancelCloudCubscription} />
              )}
              <StyledLink href="">{getCancelActionLabel(accountDetails) || ''}</StyledLink>
            </StyledTextValueWrapper>
          </StyledFlex>
          {!accountDetails?.extendedTrial && (
            <DetailsRow
              dataCy="end-date"
              label={getEndDateLabel(accountDetails)}
              value={
                <FormattedDate
                  value={new Date(accountDetails?.latestPeriod?.endDate)}
                  day="2-digit"
                  month="long"
                  year="numeric"
                />
              }
            />
          )}
          <PaymentDetailsSection accountDetails={accountDetails} productKey={productKey} />
        </>
      )}
    </>
  );
};

const ContactsSection = ({ technicalContacts, billingContacts }) => {
  return (
    <>
      <h3>
        <FormattedMessage {...messages.contactDetails} />
      </h3>
      <SectionMessage appearance="information">
        <FormattedMessage {...messages.technicalAddressOnInvoice} />
      </SectionMessage>
      <StyledDivWithBorder>
        <StyledContactsHeading>
          <FormattedMessage {...messages.technicalContacts} />
        </StyledContactsHeading>
      </StyledDivWithBorder>
      <ContactsWrapper>
        <StyledFlex flexDirection>
          {technicalContacts?.map((contact: { name: string; email: string }) => (
            <span key={`technical ${contact.name}`}>{contact.name}</span>
          ))}
        </StyledFlex>
        <StyledFlex flexDirection>
          {technicalContacts?.map((contact: { name: string; email: string }) => (
            <span key={`technical ${contact.email}`}>{contact.email}</span>
          ))}
          <AddContactWrapper>
            <FieldWrapper>
              <Textfield name="technical-contact" type="text" />
            </FieldWrapper>
            <Button appearance="primary">
              <FormattedMessage {...messages.addBtn} />
            </Button>
          </AddContactWrapper>
        </StyledFlex>
      </ContactsWrapper>
      <StyledDivWithBorder>
        <StyledContactsHeading>
          <FormattedMessage {...messages.billingContacts} />
        </StyledContactsHeading>
      </StyledDivWithBorder>
      <ContactsWrapper>
        <StyledFlex flexDirection>
          {billingContacts?.map((contact: { name: string; email: string }) => (
            <span key={`billing ${contact.name}`}>{contact.name}</span>
          ))}
        </StyledFlex>
        <StyledFlex flexDirection>
          {billingContacts?.map((contact: { name: string; email: string }) => (
            <span key={`billing ${contact.email}`}>{contact.email}</span>
          ))}
          <AddContactWrapper>
            <FieldWrapper>
              <Textfield name="technical-contact" type="text" />
            </FieldWrapper>
            <Button appearance="primary">
              <FormattedMessage {...messages.addBtn} />
            </Button>
          </AddContactWrapper>
        </StyledFlex>
      </ContactsWrapper>
    </>
  );
};

const DetailsRowContent: React.FC<Props> = ({
  accountDetails,
  endDate,
  billingContacts,
  technicalContacts,
  productKey,
}) => {
  const shouldShowAccountURL =
    productKey !== 'atlassian.organisation' &&
    accountDetails &&
    (accountDetails.accountUrl || accountDetails.isUnclaimedCloudSite);

  const accountURLValue =
    accountDetails && accountDetails.accountUrl === '' && accountDetails.isUnclaimedCloudSite ? (
      <FormattedMessage id="mac.licenses.site.not.claimed" defaultMessage="Site not yet claimed" />
    ) : (
      accountDetails.accountUrl
    );

  const isAccountHosted = hostedAccountTypes.includes(
    accountDetails?.merchandise?.productPackage?.productType,
  );

  const shouldShowAddressDetails =
    !accountDetails?.txAMigrated &&
    (productKey === 'atlassian.cloud' || productKey === 'atlassian.organisation');

  const excludedAccountKeys = [
    'atlassian.ondemand',
    'pricingplan.atlassian.ondemand',
    'atlassian.cloud',
  ];

  const showAutomaticallyChargedCardMessage =
    !excludedAccountKeys.includes(productKey) &&
    accountDetails?.renewalAction === 'AUTO_RENEW' &&
    accountDetails?.creditCardData;

  const showContactUsToRenewMessage =
    !excludedAccountKeys.includes(productKey) && accountDetails?.renewalAction !== 'AUTO_RENEW';

  const currentDate = new Date();

  const showContactUsForRenewal = new Date(accountDetails?.latestPeriod?.endDate) > currentDate;

  return (
    <>
      <StyledTableData paddingLeft colSpan={3}>
        <StyledTDSection>
          <StyledSenInfoWrapper>
            <DetailsRow
              label={<FormattedMessage {...messages.name} />}
              value={accountDetails?.name}
            />
            <DetailsRow
              label={<FormattedMessage {...messages.SEN} />}
              value={accountDetails?.id ? `SEN-${accountDetails.id}` : ''}
              dataCy="mac-sen-details"
            />
            {shouldShowAccountURL && (
              <DetailsRow label={<FormattedMessage {...messages.URL} />} value={accountURLValue} />
            )}
            {accountDetails && isAccountHosted && (
              <HostedAccountsDetails accountDetails={accountDetails} productKey={productKey} />
            )}
            {shouldShowAddressDetails && (
              <StyledFlex width={`${Dimensions.units(12.5)}%`}>
                <StyledTextLabel data-cy="mac-addres-details">
                  <FormattedMessage {...messages.addressDetails} />
                </StyledTextLabel>
                <StyledLink
                  href={`${config.adminHubUrl}/${
                    productKey !== 'atlassian.organisation' ? 's' : 'o'
                  }/${accountDetails?.externalUuid}/billing/paymentdetails`}
                >
                  <FormattedMessage {...messages.updateAddressDetails} />
                </StyledLink>
              </StyledFlex>
            )}
            {showAutomaticallyChargedCardMessage && (
              <SectionMessage>
                <FormattedMessage
                  {...messages.automaticallyChargedCardMessage}
                  values={{
                    endDate: (
                      <FormattedDate
                        value={new Date(accountDetails?.latestPeriod?.endDate)}
                        day="2-digit"
                        month="long"
                        year="numeric"
                      />
                    ),
                  }}
                />
              </SectionMessage>
            )}
            <br />
            {showContactUsToRenewMessage && (
              <StyledFlex width={`${Dimensions.units(12.5)}%`}>
                <SectionMessage>
                  <FormattedMessage {...messages.notAutomaticallyRenewedProduct} />
                  {showContactUsForRenewal && (
                    <FormattedMessage
                      {...messages.contactToRenew}
                      values={{
                        contactLink: (
                          <a href="https://www.atlassian.com/company/contact">
                            <FormattedMessage {...messages.contactUs} />
                          </a>
                        ),
                      }}
                    />
                  )}
                </SectionMessage>
              </StyledFlex>
            )}
          </StyledSenInfoWrapper>
          {endDate && (
            <SectionMessage appearance="information">
              <FormattedMessage
                {...messages.updateEndDateMessage}
                values={{
                  endDate: (
                    <FormattedDate
                      value={new Date(endDate)}
                      day="2-digit"
                      month="long"
                      year="numeric"
                    />
                  ),
                }}
              />
            </SectionMessage>
          )}
          <ContactsSection
            technicalContacts={technicalContacts}
            billingContacts={billingContacts}
          />
        </StyledTDSection>
      </StyledTableData>
      <StyledTableData colSpan={2}>
        <StyledTDSection>
          <StyledFlex>
            <StyledTextLabel width={`${Dimensions.units(3.5)}%`}>
              <FormattedMessage {...messages.organization} />
            </StyledTextLabel>
            <StyledTextValueWrapper>
              <StyledSpan margin={`0 0 0 ${Dimensions.units(1)}px`}>
                {accountDetails?.name}
              </StyledSpan>
            </StyledTextValueWrapper>
          </StyledFlex>
          <StyledFlex>
            <StyledTextLabel width={`${Dimensions.units(3.5)}%`}>
              <FormattedMessage {...messages.licenseKey} />
            </StyledTextLabel>
            <StyledLink>
              <FormattedMessage {...messages.viewLicense} />
            </StyledLink>
          </StyledFlex>
          <StyledFlex>
            <StyledTextLabel width={`${Dimensions.units(3.5)}%`}>
              <FormattedMessage {...messages.actions} />
            </StyledTextLabel>
            <StyledButtonsWrapper>
              <StyledLink>
                <FormattedMessage {...messages.upgrade} />
              </StyledLink>
              <StyledPipeSymbol>|</StyledPipeSymbol>
              <StyledLink>
                <FormattedMessage
                  {...messages.download}
                  values={{
                    description: accountDetails?.merchandise?.details?.description,
                  }}
                />
              </StyledLink>
              <StyledPipeSymbol>|</StyledPipeSymbol>
              <StyledLink>
                <FormattedMessage {...messages.downloadSource} />
              </StyledLink>
              <StyledPipeSymbol>|</StyledPipeSymbol>
              <StyledLink>
                <FormattedMessage {...messages.viewDeveloperLicense} />
              </StyledLink>
              <StyledPipeSymbol>|</StyledPipeSymbol>
              <StyledLink>
                <FormattedMessage {...messages.archive} />
              </StyledLink>
            </StyledButtonsWrapper>
          </StyledFlex>
          <StyledTextLabel width={`${Dimensions.units(3.5)}%`}>
            <FormattedMessage {...messages.history} />
          </StyledTextLabel>
        </StyledTDSection>
      </StyledTableData>
    </>
  );
};

export const LicensesExpandedRow = ({ productKey, accountId }) => {
  const [canUserSeeAccountDetails, setCanUserSeeAccountDetails] = useState(false);
  const detailsData: any = useSelector((state) => selectDetails(state as State, accountId));
  const accountDetails = detailsData && detailsData === 'Access denied' ? null : detailsData;

  const technicalContacts: Array<{ name: string; email: string }> = [];
  const billingContacts: Array<{ name: string; email: string }> = [];

  let endDate;

  useEffect(() => {
    setCanUserSeeAccountDetails(!(detailsData && detailsData === 'Access denied'));
  }, [detailsData]);

  getContacts(accountDetails, ContactTypes.TECHNICAL, technicalContacts);
  getContacts(accountDetails, ContactTypes.BILLING, billingContacts);

  if (accountDetails?.latestLicense) {
    endDate = new Date(accountDetails.latestLicense.expires);
  }

  return (
    <>
      {!canUserSeeAccountDetails ? (
        // TODO: Show the proper error message once we have it: https://hello.jira.atlassian.cloud/browse/CHT-2819
        <p>You don&apos;t have permissions to see the details</p>
      ) : (
        accountDetails && (
          <DetailsRowContent
            accountDetails={accountDetails}
            canUserSeeAccountDetails={canUserSeeAccountDetails}
            endDate={endDate}
            technicalContacts={technicalContacts}
            billingContacts={billingContacts}
            productKey={productKey}
          />
        )
      )}
    </>
  );
};
